/**
 * VideoGrid Component
 *
 * YouTube video display with performance optimization via facade pattern.
 * - Single video: centered display
 * - Multiple videos: carousel display
 * - Uses YoutubeLite for improved performance (loads on interaction)
 */

"use client";

import * as React from "react";
import {
	Carousel,
	type CarouselApi,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from "@/components/ui/carousel";
import { YoutubeLite } from "@/components/ui/youtube-lite";
import { cn, hashString } from "@/lib/utils";
import type { ComponentVideoGrid } from "@/lib/validation";

export interface VideoGridProps extends ComponentVideoGrid {
	className?: string;
}

export function VideoGrid({
	className,
	heading,
	content,
	youtubeids,
	options,
}: VideoGridProps) {
	const [api, setApi] = React.useState<CarouselApi>();
	const [selectedIndex, setSelectedIndex] = React.useState(0);
	const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

	React.useEffect(() => {
		if (!api) return;
		const onSelect = () => setSelectedIndex(api.selectedScrollSnap());
		const onReInit = () => {
			setScrollSnaps(api.scrollSnapList());
			setSelectedIndex(api.selectedScrollSnap());
		};
		setScrollSnaps(api.scrollSnapList());
		setSelectedIndex(api.selectedScrollSnap());
		api.on("select", onSelect);
		api.on("reInit", onReInit);
		return () => {
			api.off("select", onSelect);
			api.off("reInit", onReInit);
		};
	}, [api]);

	if (!youtubeids || youtubeids.length === 0) return null;

	const isGrey = options?.includes("grey");

	return (
		<section
			className={cn(
				"py-12 md:py-16 lg:py-20",
				isGrey && "bg-muted/50",
				className,
			)}
		>
			<div className="container mx-auto px-4">
				{/* Section Header */}
				{(heading || content) && (
					<div className="mb-12 text-center">
						{heading && (
							<h2 className="mb-4 text-2xl font-bold md:text-3xl lg:text-4xl">
								{heading}
							</h2>
						)}
						{content && (
							<p className="mx-auto max-w-2xl text-base text-muted-foreground md:text-lg">
								{content}
							</p>
						)}
					</div>
				)}

				{/* Single Video - Centered */}
				{youtubeids.length === 1 && (
					<div className="flex justify-center">
						<div className="w-full max-w-3xl">
							<YoutubeLite
								videoId={youtubeids[0]}
								title={heading || "Video"}
								autoLoad={false}
							/>
						</div>
					</div>
				)}

				{/* Multiple Videos - Carousel */}
				{youtubeids.length > 1 && (
					<Carousel
						opts={{
							align: "center",
							loop: true,
						}}
						setApi={setApi}
						className="mx-auto w-full max-w-5xl"
					>
						<CarouselContent>
							{youtubeids.map((videoId) => {
								// Generate stable key using hash to handle potential duplicate IDs
								const key = hashString(videoId);
								return (
									<CarouselItem key={key}>
										<YoutubeLite
											videoId={videoId}
											title={heading || "Video"}
											autoLoad={false}
										/>
									</CarouselItem>
								);
							})}
						</CarouselContent>
						<CarouselPrevious />
						<CarouselNext />
						{/* Dot indicators - mobile only */}
						<div className="mt-4 flex justify-center gap-3 md:hidden">
							{scrollSnaps.map((snap, index) => (
								<button
									key={snap}
									type="button"
									onClick={() => api?.scrollTo(index)}
									style={{
										width: 16,
										height: 16,
										backgroundColor:
											index === selectedIndex
												? "var(--primary)"
												: "var(--muted-foreground)",
									}}
									className="rounded-full transition-colors"
									aria-label={`Go to slide ${index + 1}`}
								/>
							))}
						</div>
					</Carousel>
				)}
			</div>
		</section>
	);
}
